import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { ConfBadge, EmptyState, PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function Notifications() {
  const [rows, setRows] = useState([]);
  const [emailOn, setEmailOn] = useState(true);

  const load = () => api.get("/notifications").then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    load();
    api.get("/notifications/preferences").then(({ data }) => setEmailOn(data.email_notifications)).catch(() => {});
  }, []);

  const togglePref = async (v) => {
    setEmailOn(v);
    await api.put("/notifications/preferences", { email_notifications: v }).catch((e) => toast.error(errMsg(e)));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        testid="notifications-header"
        eyebrow="Inbox"
        title="Notifications"
        description="Restricted items are labelled but never expose their content in alerts or email."
        actions={
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-3 text-xs">
              <Switch data-testid="email-notifications-switch" checked={emailOn} onCheckedChange={togglePref} />
              Email copies
            </label>
            <Button
              data-testid="mark-all-read-button"
              variant="outline"
              className="rounded-none text-[10px] uppercase tracking-[0.15em]"
              onClick={async () => {
                await api.post("/notifications/read-all");
                load();
              }}
            >
              Mark all read
            </Button>
          </div>
        }
      />

      <div className="panel divide-y divide-border" data-testid="notifications-list">
        {rows.map((n) => (
          <div key={n.id} className={`flex flex-wrap items-start justify-between gap-4 px-5 py-4 ${n.read ? "opacity-60" : ""}`}>
            <div className="min-w-0">
              <p className="text-sm">{n.title}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {n.confidentiality >= 3 ? "Restricted item — sign in to the workspace to view details." : n.message}
              </p>
              <p className="label-caps mt-2">{new Date(n.created_at).toLocaleString()}</p>
            </div>
            <div className="flex items-center gap-3">
              <ConfBadge level={n.confidentiality} />
              {!n.read && (
                <Button
                  data-testid={`mark-read-${n.id}`}
                  size="sm"
                  variant="ghost"
                  className="rounded-none text-[10px] uppercase tracking-[0.15em]"
                  onClick={async () => {
                    await api.post(`/notifications/${n.id}/read`);
                    load();
                  }}
                >
                  Mark read
                </Button>
              )}
            </div>
          </div>
        ))}
        {rows.length === 0 && <EmptyState testid="notifications-empty" title="Inbox empty" message="No notifications yet." />}
      </div>
    </div>
  );
}
